import TransferTab from './TransferTab';
import SEOManager from './SEOManager';
import ProtectedSection from './ProtectedSection';
import AccessControl from './AccessControl';
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../api/client';
import ModuleAccessPicker from './ModuleAccessPicker';
import InactiveUsersEmail from './InactiveUsersEmail';
import PromoAnalyticsPanel from './PromoAnalyticsPanel';
import ArchivePanel from './ArchivePanel';
import SystemErrorsPanel from './SystemErrorsPanel';
import SystemMonitoring from './SystemMonitoring';
import AuditPanel from './AuditPanel';
import BillingPanel from './BillingPanel';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import StorageCell from './StorageCell';
import {
  X, Trash2, KeyRound, Lock, Unlock, Pencil, Gift,
  Eye, EyeOff, Clock, ArrowLeft,
  MessageCircle, Search, Send, Filter, ChevronDown, User as UserIcon, CheckCircle, Circle
} from 'lucide-react';

// ==================== Типы ====================
interface User {
  id: string;
  name: string;
  email: string;
  role: 'user' | 'admin' | 'superadmin' | 'hr_manager' | 'recruiter' | 'employee' | 'sales_manager' | 'accountant' | 'kdp';
  blocked: boolean;
  companyId?: string;
  companyName?: string;
  company: string;
  paid: boolean;
  demoExpiresAt?: string;
  createdAt?: string;
  lastActive?: string;
  paymentStatus?: 'none' | 'pending' | 'paid';
  tariffPlan?: 'month' | 'quarter' | 'year';
  paidUntil?: string;
  passwordChangedAt?: string | null; // <-- добавлено поле даты смены пароля
  lastEmailSentAt?: string | null;
  lastEmailTemplateName?: string | null;
  deletionWarningAt?: string | null;
  storageUsed?: number;
  storageLimit?: number;

}

interface Company {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  storageUsed?: number;
  storageLimit?: number;

}

interface Ticket {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  subject: string;
  message: string;
  status: 'open' | 'closed';
  replies: Array<{ from: string; message: string; timestamp: string }>;
  createdAt: string;
  updatedAt: string;
}

interface EditUserForm {
  name: string;
  email: string;
  role: 'user' | 'admin' | 'superadmin' | 'hr_manager' | 'recruiter' | 'employee' | 'sales_manager' | 'accountant' | 'kdp';
  companyId: string;
  company: string;
  password: string;
}

type Tab = 'accessControl' | 'seo' | 'users' | 'tickets' | 'companies' | 'xml' | 'inactiveEmails' | 'promo' | 'archive' | 'systemErrors' | 'monitoring' | 'audit' | 'billing';

const trimSheetName = (name: string): string => {
  if (name.length <= 31) return name;
  return name.substring(0, 30) + '…';
};

// ==================== Компонент ====================
const AdminPanel: React.FC = () => {
  const navigate = useNavigate();
  const { user: currentUser, updateUser } = useAuth();

  // Фолбэк: если currentUser еще не загружен, берем isMainAdmin из localStorage
  const isMainAdmin = currentUser?.isMainAdmin || currentUser?.email === 'admin@hrlama.ru' || (() => {
    try {
      const parsed = JSON.parse(localStorage.getItem('user') || '{}');
      return parsed?.isMainAdmin === true || parsed?.email === 'admin@hrlama.ru';
    } catch { return false; }
  })();



  
  const checkAccess = (sectionId: string) => {
    if (isMainAdmin) return true;
    console.log('⛔ checkAccess failed:', sectionId, '| isMainAdmin:', isMainAdmin, '| currentUser:', currentUser);
    if (!currentUser) return false;
    if (currentUser.role !== 'superadmin') return true;
    if (!currentUser.adminPermissions || currentUser.adminPermissions.length === 0) return false;
    return currentUser.adminPermissions.includes(sectionId);
  };




  // ----- Общие состояния -----
  const [activeTab, setActiveTab] = useState<Tab>('users');

  useEffect(() => {
    if (currentUser && activeTab !== 'accessControl') {
      if (!checkAccess(activeTab)) {
        const allTabs: Tab[] = ['users', 'companies', 'tickets', 'xml', 'seo', 'inactiveEmails', 'archive', 'systemErrors', 'monitoring', 'audit', 'promo', 'billing'];
        const firstAllowed = allTabs.find(tab => checkAccess(tab));
        if (firstAllowed) {
          setActiveTab(firstAllowed);
        }
      }
    }
  }, [currentUser, activeTab]);
  const [promoBadgeCount, setPromoBadgeCount] = useState(0);
  const [promoTotalActivated, setPromoTotalActivated] = useState(0);

  // ★ Бейдж «Промо»: показываем, сколько активаций с момента последнего просмотра
  useEffect(() => {
    if (currentUser?.role !== 'superadmin') return;
    api.get('/promo/analytics').then(res => {
      const total = res.data.stats?.totalActivated || 0;
      setPromoTotalActivated(total);
      const readCount = parseInt(localStorage.getItem('promoBadgeRead') || '0', 10);
      if (total > readCount) setPromoBadgeCount(total - readCount);
    }).catch(() => {});
  }, [currentUser]);

  const [systemErrorsCount, setSystemErrorsCount] = useState(0);
  const [lastTicketCount, setLastTicketCount] = useState<number>(0);
  const [lastCheckedUserDate, setLastCheckedUserDate] = useState<Date | null>(null);

  // ----- Пользователи -----
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetUserId, setResetUserId] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState('');

  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    password: '',
    role: 'user' as 'user' | 'admin' | 'superadmin' | 'hr_manager' | 'recruiter' | 'employee' | 'sales_manager' | 'accountant' | 'kdp',
    companyId: '',
    company: '',
    tariffPlan: 'month' as 'month' | 'quarter' | 'year',
    paidUntil: '',
    demoExpiresAt: '',
    paymentStatus: 'none' as 'none' | 'pending' | 'paid',
  });

  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [editForm, setEditForm] = useState<EditUserForm>({
    name: '', email: '', role: 'user', companyId: '', company: '', password: '',
  });
  const [showEditPassword, setShowEditPassword] = useState(false);
  const [editTariffPlan, setEditTariffPlan] = useState<'month' | 'quarter' | 'year'>('month');
  const [editPaidUntil, setEditPaidUntil] = useState<string>('');
  const [editDemoExpiresAt, setEditDemoExpiresAt] = useState<string>('');

  // ----- Фильтры -----
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPaid, setFilterPaid] = useState<'all' | 'paid' | 'pending' | 'unpaid'>('all');
  const [filterPaidCheckbox, setFilterPaidCheckbox] = useState<boolean>(false);
  const [filterCompany, setFilterCompany] = useState<string>('all');
  const [filterRole, setFilterRole] = useState<'all' | 'user' | 'admin' | 'superadmin' | 'hr_manager' | 'recruiter' | 'employee' | 'sales_manager' | 'accountant' | 'kdp'>('all');
  const [filterBlocked, setFilterBlocked] = useState<'all' | 'active' | 'blocked'>('all');
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
  const [filterOverdue, setFilterOverdue] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
  const [sortKey, setSortKey] = useState<'createdAt' | 'lastActive' | 'name' | 'email' | 'storageUsed'>('createdAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const handleSort = (key: 'createdAt' | 'lastActive' | 'name' | 'email' | 'storageUsed') => {
    if (sortKey === key) {
      setSortDir(sortDir === 'desc' ? 'asc' : 'desc');
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  // ----- Компании -----
  const [companies, setCompanies] = useState<Company[]>([]);
  const [companySearch, setCompanySearch] = useState('');
  const [expandedCompanyId, setExpandedCompanyId] = useState<string | null>(null);
  const [companySortDir, setCompanySortDir] = useState<'asc' | 'desc' | null>(null);
  const [companySortKey, setCompanySortKey] = useState<'userCount' | 'storageUsed'>('userCount');
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [showCompanyDeleteModal, setShowCompanyDeleteModal] = useState(false);
  const [deleteCompanyData, setDeleteCompanyData] = useState<{ id: string; name: string; related: any } | null>(null);
  const [deleteCompanyLoading, setDeleteCompanyLoading] = useState(false);
  const [forceDeleteConfirmed, setForceDeleteConfirmed] = useState(false);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const [companyForm, setCompanyForm] = useState({ name: '' });
  const [companyModules, setCompanyModules] = useState({
    canViewSalesCRM: false,
    canViewHRM: false,
    canViewAccountingCRM: false,
    canViewHRDocuments: false,
    canViewDataDriven: false,
  });
  const [loadingCompanyModules, setLoadingCompanyModules] = useState(false);

  // ----- Тикеты -----
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [adminReply, setAdminReply] = useState('');
  const [ticketFilter, setTicketFilter] = useState<'all' | 'open' | 'closed'>('all');
  const [ticketSearch, setTicketSearch] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const handleTicketSelect = (ticket: Ticket) => {
    setSelectedTicket(ticket);
    setAdminReply('');
  };

  // ----- XML -----
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>('');
  const [importType, setImportType] = useState<string>('candidates');
  const [xmlFile, setXmlFile] = useState<File | null>(null);
  const [xmlExportLoading, setXmlExportLoading] = useState(false);
  const [xmlImportLoading, setXmlImportLoading] = useState(false);

  // ----- Статистика -----
  const [onlineUsers, setOnlineUsers] = useState<string[]>([]);
  const [stats, setStats] = useState({
    totalUsers: 0, activeUsers: 0, paidUsers: 0, demoActive: 0,
    newUsers7d: 0, openTickets: 0, closedTickets: 0, paidStatusCount: 0,
  });
  const [dateFrom, setDateFrom] = useState<string>(() => {
    const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().split('T')[0];
  });
  const [dateTo, setDateTo] = useState<string>(() => new Date().toISOString().split('T')[0]);

  // ----- Диалоги -----
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean; title: string; message: string; onConfirm: () => void;
  } | null>(null);
  const [alertDialog, setAlertDialog] = useState<{
    isOpen: boolean; title: string; message: string;
  } | null>(null);

  const showConfirm = (title: string, message: string, onConfirm: () => void) => {
    setConfirmDialog({ isOpen: true, title, message, onConfirm });
  };
  const showAlert = (title: string, message: string) => {
    setAlertDialog({ isOpen: true, title, message });
  };

  // ==================== Вспомогательные функции ====================
  const recalcPaidUntil = (plan: 'month' | 'quarter' | 'year') => {
    const from = new Date();
    let to = new Date(from);
    switch (plan) {
      case 'month': to.setMonth(to.getMonth() + 1); break;
      case 'quarter': to.setMonth(to.getMonth() + 3); break;
      case 'year': to.setFullYear(to.getFullYear() + 1); break;
    }
    return to.toISOString().split('T')[0];
  };

  const extendDemo = async (userId: string) => {
    const fiveDaysLater = new Date(); fiveDaysLater.setDate(fiveDaysLater.getDate() + 5);
    try {
      await api.put(`/users/${userId}`, { demoExpiresAt: fiveDaysLater.toISOString(), paid: true, paymentStatus: 'paid' });
      await fetchUsers(true);
      showAlert('Успех', 'Демо-доступ продлён на 5 дней');
    } catch (err) { showAlert('Ошибка', 'Не удалось продлить демо-доступ'); }
  };

  const extendPaidPeriod = async (userId: string) => {
    const user = users.find(u => u.id === userId);
    if (!user) return;
    const plan = user.tariffPlan || 'month';
    const from = user.paidUntil ? new Date(user.paidUntil) : new Date();
    let to = new Date(from);
    switch (plan) {
      case 'month': to.setMonth(to.getMonth() + 1); break;
      case 'quarter': to.setMonth(to.getMonth() + 3); break;
      case 'year': to.setFullYear(to.getFullYear() + 1); break;
    }
    const newPaidUntil = to.toISOString();
    try {
      await api.put(`/users/${userId}`, { paidUntil: newPaidUntil, paymentStatus: 'paid', paid: true });
      await fetchUsers(true);
    } catch (err) { showAlert('Ошибка', 'Не удалось продлить тариф'); }
  };

  // ==================== Загрузка данных ====================
  const fetchUsers = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await api.get('/users');
      const usersData: User[] = res.data;
      setUsers(usersData);
      const now = new Date();
      const sevenDaysAgo = new Date(now); sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      const active = usersData.filter(u => !u.blocked).length;
      const paid = usersData.filter(u => u.paid).length;
const paidStatusCount = usersData.filter(u => u.paymentStatus === 'paid').length;
      const demoActive = usersData.filter(u => u.demoExpiresAt && new Date(u.demoExpiresAt) > now).length;
      const newUsers7d = usersData.filter(u => u.createdAt && new Date(u.createdAt) > sevenDaysAgo).length;
      setStats(prev => ({ ...prev, totalUsers: usersData.length, activeUsers: active, paidUsers: paid,paidStatusCount, demoActive, newUsers7d }));
      if (lastCheckedUserDate) {
        const newUsers = usersData.filter(u => u.createdAt && new Date(u.createdAt) > lastCheckedUserDate);
        if (newUsers.length > 0 && Notification.permission === 'granted') {
          new Notification('Новые пользователи', { body: `Зарегистрировано ${newUsers.length} новых пользователей.` });
        }
      }
      const maxDate = usersData.reduce((max: Date, u) => u.createdAt && new Date(u.createdAt) > max ? new Date(u.createdAt) : max, new Date(0));
      setLastCheckedUserDate(maxDate);
    } catch (err: any) { showAlert('Ошибка', 'Не удалось загрузить список пользователей'); }
    finally { if (!silent) setLoading(false); }
  };

  const fetchCompanies = async () => {
    setLoadingCompanies(true);
    try { const res = await api.get('/companies'); setCompanies(res.data); }
    catch (err) { showAlert('Ошибка', 'Не удалось загрузить компании'); }
    finally { setLoadingCompanies(false); }
  };

  const fetchTickets = useCallback(async () => {
    try {
      const res = await api.get('/tickets');
      const newTickets = res.data;
      const newCount = newTickets.length;
      if (lastTicketCount > 0 && newCount > lastTicketCount) {
        const diff = newCount - lastTicketCount;
        if (Notification.permission === 'granted') {
          new Notification('Новые тикеты', { body: `Поступило ${diff} новых обращений.` });
        }
      }
      setTickets(newTickets);
      setLastTicketCount(newCount);
      setStats(prev => ({
        ...prev,
        openTickets: newTickets.filter((t: Ticket) => t.status === 'open').length,
        closedTickets: newTickets.filter((t: Ticket) => t.status === 'closed').length,
      }));
    } catch (err) { console.error('Ошибка загрузки тикетов', err); }
  }, [lastTicketCount]);

  // 🚨 Polling счётчика системных ошибок
  useEffect(() => {
    const fetchCount = () => {
      api.get('/system-errors/count')
        .then(r => setSystemErrorsCount(r.data.total || 0))
        .catch(() => {});
    };
    fetchCount();
    const t = setInterval(fetchCount, 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => { fetchTickets(); const interval = setInterval(fetchTickets, 10000); return () => clearInterval(interval); }, [fetchTickets]);
  useEffect(() => { if (activeTab === 'users') fetchUsers(); else if (activeTab === 'companies') { fetchCompanies(); fetchUsers(true); } }, [activeTab]); 
  useEffect(() => { const interval = setInterval(() => { if (activeTab === 'users') fetchUsers(true); }, 30000); return () => clearInterval(interval); }, [activeTab]);
  useEffect(() => { fetchCompanies(); }, []);

  // ==================== Действия с пользователями ====================
  const handleRoleChange = async (userId: string, newRole: string) => {
    try { await api.put(`/users/${userId}`, { role: newRole }); await fetchUsers(true); }
    catch (err) { showAlert('Ошибка', 'Не удалось изменить роль'); }
  };
  const handleBlockToggle = async (userId: string) => {
    const user = users.find(u => u.id === userId); if (!user) return;
    try { await api.put(`/users/${userId}`, { blocked: !user.blocked }); await fetchUsers(true); }
    catch (err) { showAlert('Ошибка', 'Не удалось изменить статус блокировки'); }
  };
  const toggleUserSelection = (id: string) => {
    setSelectedUserIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAllUsers = () => {
    if (selectedUserIds.size === filteredUsersForTable.length) {
      setSelectedUserIds(new Set());
    } else {
      setSelectedUserIds(new Set(filteredUsersForTable.map(u => u.id)));
    }
  };

  const handleBulkBlock = async (blocked: boolean) => {
    if (selectedUserIds.size === 0) return alert('Выберите пользователей');
    await api.post('/users/bulk-block', { userIds: Array.from(selectedUserIds), blocked });
    await fetchUsers(true);
    setSelectedUserIds(new Set());
  };

  const handleBulkDelete = () => {
    if (selectedUserIds.size === 0) return alert('Выберите пользователей');
    setShowBulkDeleteModal(true);
  };

  const confirmBulkDelete = async () => {
    setBulkDeleteLoading(true);
    try {
      await api.post('/users/bulk-delete', { userIds: Array.from(selectedUserIds) });
      setSelectedUserIds(new Set());
      await fetchUsers(true);
      setShowBulkDeleteModal(false);
    } catch (e: any) {
      alert(e?.response?.data?.error || 'Ошибка удаления');
    } finally {
      setBulkDeleteLoading(false);
    }
  };

  const handleBulkAssignCompany = async (companyId: string) => {
    if (selectedUserIds.size === 0) return alert('Выберите пользователей');
    if (!companyId) return;
    try {
      await api.post('/users/bulk-assign-company', {
        userIds: Array.from(selectedUserIds),
        companyId,
      });
      await fetchUsers(true);
      setSelectedUserIds(new Set());
    } catch (err: any) {
      alert(err?.response?.data?.error || err?.message || 'Ошибка назначения компании');
    }
  };
  const handleDelete = (userId: string) => {
    if (userId === currentUser?.id) { showAlert('Ошибка', 'Нельзя удалить самого себя'); return; }
    showConfirm('Удаление пользователя', 'Вы уверены?', async () => {
      try { await api.delete(`/users/${userId}`); await fetchUsers(true); }
      catch (err) { showAlert('Ошибка', 'Не удалось удалить пользователя'); }
    });
  };
  const handlePaidToggle = async (userId: string) => {
    const u = users.find(x => x.id === userId); if (!u) return;
    try { const res = await api.put(`/users/${userId}`, { paid: !u.paid }); await fetchUsers(true); if (currentUser?.id === userId) updateUser({ paid: res.data.user.paid }); }
    catch (err) { showAlert('Ошибка', 'Не удалось изменить статус оплаты'); }
  };
  const handlePaymentStatusChange = async (userId: string, newStatus: string) => {
    try {
      const res = await api.put(`/users/${userId}`, { paymentStatus: newStatus, paid: newStatus === 'paid' });
      if (res.data?.user) {
        setUsers(prev => prev.map(u => u.id === userId ? { ...u, paymentStatus: res.data.user.paymentStatus, paid: res.data.user.paid } : u));
      } else {
        setUsers(prev => prev.map(u => u.id === userId ? { ...u, paymentStatus: newStatus as any, paid: newStatus === 'paid' } : u));
      }
      fetchUsers(true);
    } catch (err) { showAlert('Ошибка', 'Не удалось изменить статус оплаты'); }
  };
  const handleResetPassword = (userId: string) => {
    if (userId === currentUser?.id) { showAlert('Ошибка', 'Нельзя сбросить пароль самому себе'); return; }
    setResetUserId(userId); setNewPassword(''); setShowResetModal(true);
  };
  const confirmResetPassword = async () => {
    if (!resetUserId || !newPassword.trim()) { showAlert('Ошибка', 'Введите новый пароль'); return; }
    try {
      await api.put(`/users/${resetUserId}/reset-password`, { password: newPassword });
      showAlert('Успех', 'Пароль изменён');
      setShowResetModal(false);
      setResetUserId(null);
      setNewPassword('');
      await fetchUsers(true);
    } catch (err) { showAlert('Ошибка', 'Не удалось сбросить пароль'); }
  };

  // ----- Добавление / редактирование пользователя -----
  const handleAddUser = async () => {
    if (!newUser.name || !newUser.email || !newUser.password || !newUser.companyId) {
      showAlert('Ошибка', 'Заполните имя, email, пароль и выберите компанию');
      return;
    }
    try {
      await api.post('/users', {
        name: newUser.name,
        email: newUser.email,
        password: newUser.password,
        role: newUser.role,
        companyId: newUser.companyId,
        company: newUser.company,
        tariffPlan: newUser.tariffPlan,
        paidUntil: newUser.paidUntil || null,
        demoExpiresAt: newUser.demoExpiresAt || null,
        paymentStatus: newUser.paymentStatus,
        paid: newUser.paymentStatus === 'paid',
      });
      await fetchUsers(true);
      setNewUser({ name: '', email: '', password: '', role: 'user', companyId: '', company: '', tariffPlan: 'month', paidUntil: '', demoExpiresAt: '', paymentStatus: 'none' });
      setShowAddModal(false);
    } catch (err) { showAlert('Ошибка', 'Не удалось добавить пользователя'); }
  };

  const openEditModal = (user: User) => {
    if (companies.length === 0) fetchCompanies();
    setEditingUser(user);
    const compName = companies.find(c => c.id === user.companyId)?.name || user.companyName || user.company || ''; setEditForm({ name: user.name, email: user.email, role: user.role, companyId: user.companyId || '', company: compName, password: '' });
    setEditTariffPlan(user.tariffPlan || 'month');
    setEditPaidUntil(user.paidUntil ? user.paidUntil.split('T')[0] : '');
    setEditDemoExpiresAt(user.demoExpiresAt ? user.demoExpiresAt.split('T')[0] : '');
    setShowEditPassword(false);
    setShowEditModal(true);
  };

  const handleEditUser = async () => {
    if (!editForm.name || !editForm.email || !editForm.companyId) {
      showAlert('Ошибка', 'Заполните имя, email и выберите компанию');
      return;
    }
    try {
      const payload: any = {
        name: editForm.name,
        email: editForm.email,
        role: editForm.role,
        companyId: editForm.companyId,
        company: editForm.company || '',
        tariffPlan: editTariffPlan,
        paidUntil: editPaidUntil || null,
        demoExpiresAt: editDemoExpiresAt || null,
      };
      if (editForm.password && editForm.password.trim() !== '') {
        payload.password = editForm.password;
      }
      await api.put(`/users/${editingUser!.id}`, payload);
      window.location.reload();
      if (currentUser?.id === editingUser!.id) {
        updateUser({ name: editForm.name, email: editForm.email, role: editForm.role, companyId: editForm.companyId });
      }
      setShowEditModal(false); setEditingUser(null);
    } catch (err) { showAlert('Ошибка', 'Не удалось сохранить изменения'); }
  };

  // ----- Компании -----
  const handleAddCompany = async () => {
    if (!companyForm.name.trim()) { showAlert('Ошибка', 'Введите название'); return; }
    try {
      const resp = await api.post('/companies', { name: companyForm.name });
      const newCompanyId = resp.data?.id || resp.data?._id;
      if (currentUser?.role === 'superadmin' && newCompanyId) {
        try {
          await api.put(`/users/companies/${newCompanyId}/modules`, {
            modules: {
              canViewSalesCRM: !!companyModules.canViewSalesCRM,
              canViewHRM: !!companyModules.canViewHRM,
              canViewAccountingCRM: !!companyModules.canViewAccountingCRM,
              canViewHRDocuments: !!companyModules.canViewHRDocuments,
              canViewDataDriven: !!companyModules.canViewDataDriven,
            },
          });
        } catch (e) { console.warn('Не удалось сохранить модули:', e); }
      }
      await fetchCompanies();
      setShowCompanyModal(false);
      setCompanyForm({ name: '' });
      setCompanyModules({ canViewSalesCRM: false, canViewHRM: false, canViewAccountingCRM: false, canViewHRDocuments: false, canViewDataDriven: false });
    }
    catch (err) { showAlert('Ошибка', 'Не удалось создать компанию'); }
  };
  const handleUpdateCompany = async () => {
    if (!companyForm.name.trim() || !editingCompany) return;
    try {
      await api.put(`/companies/${editingCompany.id}`, { name: companyForm.name });
      if (currentUser?.role === 'superadmin') {
        try {
          await api.put(`/users/companies/${editingCompany.id}/modules`, {
            modules: {
              canViewSalesCRM: !!companyModules.canViewSalesCRM,
              canViewHRM: !!companyModules.canViewHRM,
              canViewAccountingCRM: !!companyModules.canViewAccountingCRM,
              canViewHRDocuments: !!companyModules.canViewHRDocuments,
              canViewDataDriven: !!companyModules.canViewDataDriven,
            },
          });
        } catch (e) { console.warn('Не удалось сохранить модули:', e); }
      }
      await fetchCompanies();
      setShowCompanyModal(false);
      setEditingCompany(null);
      setCompanyForm({ name: '' });
    }
    catch (err) { showAlert('Ошибка', 'Не удалось обновить компанию'); }
  };
  const handleDeleteCompany = async (id: string) => {
    const company = companies.find(c => c.id === id);
    if (!company) return;

    try {
      const res = await api.get(`/companies/${id}/related`);
      const related = res.data;
      const total = (related.users?.count || 0) + (related.employees?.count || 0) +
                    (related.candidates?.count || 0) + (related.vacancies?.count || 0) +
                    (related.requisitions?.count || 0);

      if (total === 0) {
        showConfirm('Удалить компанию?', `Компания «${company.name}» будет удалена.`, async () => {
          try { await api.delete(`/companies/${id}`); await fetchCompanies(); }
          catch (err: any) { showAlert('Ошибка', err.response?.data?.error || 'Не удалось удалить компанию'); }
        });
      } else {
        setDeleteCompanyData({ id, name: company.name, related });
        setForceDeleteConfirmed(false);
        setShowCompanyDeleteModal(true);
      }
    } catch (err: any) {
      showAlert('Ошибка', err.response?.data?.error || 'Не удалось проверить привязки');
    }
  };

  const confirmForceDelete = async () => {
    if (!deleteCompanyData || !forceDeleteConfirmed) return;
    setDeleteCompanyLoading(true);
    try {
      await api.delete(`/companies/${deleteCompanyData.id}?force=true`);
      await fetchCompanies();
      setShowCompanyDeleteModal(false);
      setDeleteCompanyData(null);
    } catch (err: any) {
      showAlert('Ошибка', err.response?.data?.error || 'Не удалось удалить компанию');
    } finally {
      setDeleteCompanyLoading(false);
    }
  };

  // ----- Тикеты -----

  const handleSendAdminReply = async () => {
    if (!selectedTicket || !adminReply.trim()) return;
    try {
      const res = await api.post(`/tickets/${selectedTicket.id}/reply`, { message: adminReply });
      setSelectedTicket(res.data);
      setTickets(prev => prev.map(t => t.id === res.data.id ? res.data : t));
      setAdminReply('');
      // Прокрутка к последнему сообщению только после отправки
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 100);
    } catch (err) {
      showAlert('Ошибка', 'Не удалось отправить ответ');
    }
  };
  const handleCloseTicket = async () => {
    if (!selectedTicket) return;
    try { const res = await api.put(`/tickets/${selectedTicket.id}/status`, { status: 'closed' }); setSelectedTicket(res.data); setTickets(prev => prev.map(t => t.id === res.data.id ? res.data : t)); }
    catch (err) { showAlert('Ошибка', 'Не удалось закрыть тикет'); }
  };
  const handleReopenTicket = async () => {
    if (!selectedTicket) return;
    try { const res = await api.put(`/tickets/${selectedTicket.id}/status`, { status: 'open' }); setSelectedTicket(res.data); setTickets(prev => prev.map(t => t.id === res.data.id ? res.data : t)); }
    catch (err) { showAlert('Ошибка', 'Не удалось открыть тикет'); }
  };

  // ----- Экспорт в Excel -----
  const exportStatsToExcel = async () => {
    try {
      const wb = XLSX.utils.book_new();
      
      // 1. Общая статистика
      const statsData = [
        ['Показатель', 'Значение'],
        ['Всего пользователей', stats.totalUsers], ['Активных', stats.activeUsers], ['Оплачено', stats.paidUsers],
        ['Активный демо', stats.demoActive], ['Новых за период', users.filter(u => u.createdAt && new Date(u.createdAt) >= new Date(dateFrom) && new Date(u.createdAt) <= new Date(dateTo)).length],
        ['Открытых тикетов', stats.openTickets], ['Закрытых тикетов', stats.closedTickets],
      ];
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(statsData), trimSheetName('Общая статистика'));

      // 2. Пользователи (выгружаем ВСЕХ без фильтра по датам)
      if (users.length) {
        const wsUsers = XLSX.utils.json_to_sheet(users.map(u => ({
          ID: u.id, Имя: u.name, Email: u.email, Роль: u.role,
          Компания: u.companyName || u.company, Оплачен: u.paid ? 'Да' : 'Нет',
          'Статус оплаты': u.paymentStatus || '—', 'Демо до': u.demoExpiresAt ? new Date(u.demoExpiresAt).toLocaleDateString() : 'Нет',
          'Смена пароля': (u.passwordChangedAt && u.createdAt && (new Date(u.passwordChangedAt).getTime() - new Date(u.createdAt).getTime()) > 60000) ? new Date(u.passwordChangedAt).toLocaleDateString() : 'Не менялся',
          'Дата регистрации': u.createdAt ? new Date(u.createdAt).toLocaleDateString() : '—',
        })));
        XLSX.utils.book_append_sheet(wb, wsUsers, trimSheetName(`Пользователи`));
      }

      // 3. Компании
      if (companies.length) {
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(companies.map(c => ({ ID: c.id, Название: c.name, 'Дата создания': new Date(c.createdAt).toLocaleDateString() }))), trimSheetName('Компании'));
      }

      // 4. Тикеты
      if (tickets.length) {
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(tickets.map(t => ({
          ID: t.id, Пользователь: `${t.userName} (${t.userEmail})`, Тема: t.subject, Сообщение: t.message,
          Статус: t.status === 'open' ? 'Открыт' : 'Закрыт', 'Дата создания': new Date(t.createdAt).toLocaleString(),
        }))), trimSheetName('Тикеты'));
      }

      // 5. Промо (подгружаем через API)
      try {
        const promoRes = await api.get('/promo/analytics');
        if (promoRes.data && promoRes.data.items && promoRes.data.items.length) {
          const wsPromo = XLSX.utils.json_to_sheet(promoRes.data.items.map((i: any) => ({
            'Имя': i.name || '—', 'Email': i.email, 'Компания': i.company || '—',
            'Письмо': i.promoEmailSentAt ? new Date(i.promoEmailSentAt).toLocaleString() : '—',
            'Клик': i.promoLinkClickedAt ? new Date(i.promoLinkClickedAt).toLocaleString() : '—',
            'Активация': i.promoActivatedAt ? new Date(i.promoActivatedAt).toLocaleString() : '—',
            'Окончание': i.promoEndsAt ? new Date(i.promoEndsAt).toLocaleString() : '—',
            'Статус': i.status,
          })));
          XLSX.utils.book_append_sheet(wb, wsPromo, trimSheetName('Промо 14 дней'));
        }
      } catch (e) { console.error('Ошибка выгрузки Промо', e); }

      // 6. Архив (подгружаем через API)
      try {
        const archiveRes = await api.get('/archive');
        if (archiveRes.data && archiveRes.data.length) {
          XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(archiveRes.data), trimSheetName('Архив'));
        }
      } catch (e) { console.error('Ошибка выгрузки Архива', e); }

      // 7. Ошибки системы (подгружаем через API)
      try {
        // Добавляем limit=1000, чтобы забрать все 48 ошибок, а не только 10
        const errorsRes = await api.get('/system-errors?limit=1000');
        let errorsData = [];
        
        // Универсальный парсер: проверяем все возможные варианты структуры
        if (Array.isArray(errorsRes.data)) {
          errorsData = errorsRes.data;
        } else if (errorsRes.data && Array.isArray(errorsRes.data.errors)) {
          errorsData = errorsRes.data.errors;
        } else if (errorsRes.data && Array.isArray(errorsRes.data.items)) {
          errorsData = errorsRes.data.items;
        } else if (errorsRes.data && Array.isArray(errorsRes.data.data)) {
          errorsData = errorsRes.data.data;
        }
        
        // Выводим в консоль для отладки (если вдруг опять не появится)
        console.log('Выгрузка ошибок. Найдено:', errorsData.length, 'Формат ответа:', errorsRes.data);

        if (errorsData.length > 0) {
          const wsErrors = XLSX.utils.json_to_sheet(errorsData.map((err: any) => ({
            'ID': err._id || err.id || '—',
            'Сообщение': err.message || err.error || '—',
            'URL': err.url || '—',
            'Метод': err.method || '—',
            'Пользователь': err.userEmail || err.userId || 'Аноним',
            'Дата': err.createdAt ? new Date(err.createdAt).toLocaleString() : '—',
            'Детали': err.stack ? err.stack.substring(0, 100) + '...' : '—'
          })));
          XLSX.utils.book_append_sheet(wb, wsErrors, trimSheetName('Ошибки системы'));
        }
      } catch (e) { 
        console.error('Ошибка выгрузки Ошибок', e); 
      }

      // 8. Рассылки (подгружаем через API)
      try {
        const emailsRes = await api.get('/admin/email/inactive-users');
        if (emailsRes.data && emailsRes.data.length) {
          XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(emailsRes.data), trimSheetName('Рассылки'));
        }
      } catch (e) { console.error('Ошибка выгрузки Рассылок', e); }

      // Сохраняем
      const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      saveAs(new Blob([wbout], { type: 'application/octet-stream' }), `admin_stats_${dateFrom}_to_${dateTo}.xlsx`);

    } catch (err) {
      console.error('Ошибка глобального экспорта:', err);
      alert('Не удалось сформировать Excel-файл');
    }
  };

  // ----- XML импорт / экспорт -----
  const handleExportXML = async () => {
    if (!selectedCompanyId) { showAlert('Ошибка', 'Выберите компанию'); return; }
    setXmlExportLoading(true);
    try {
      const response = await api.get('/export/xml', {
        params: { companyId: selectedCompanyId, type: importType },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${importType}_${selectedCompanyId}.xml`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) { showAlert('Ошибка', 'Не удалось экспортировать данные'); }
    finally { setXmlExportLoading(false); }
  };
  const handleImportXML = async () => {
    if (!selectedCompanyId) { showAlert('Ошибка', 'Выберите компанию'); return; }
    if (!xmlFile) { showAlert('Ошибка', 'Выберите файл для импорта'); return; }
    const formData = new FormData(); formData.append('file', xmlFile); formData.append('companyId', selectedCompanyId); formData.append('type', importType);
    setXmlImportLoading(true);
    try { await api.post('/export/xml', formData, { headers: { 'Content-Type': 'multipart/form-data' } }); showAlert('Успех', 'Импорт успешно выполнен'); setXmlFile(null); }
    catch (err) { showAlert('Ошибка', 'Не удалось импортировать данные'); }
    finally { setXmlImportLoading(false); }
  };

  // ==================== Фильтрация ====================
  

  const fetchOnlineUsers = useCallback(async () => {
    try {
      const res = await api.get('/users/online');
      setOnlineUsers(res.data.map((u: any) => u.id));
    } catch (e) { /* тихо */ }
  }, []);
  useEffect(() => {
    fetchOnlineUsers();
    const i = setInterval(fetchOnlineUsers, 30000);
    return () => clearInterval(i);
  }, [fetchOnlineUsers]);

const getDeletionStyle = (u: { deletionWarningAt?: string | null }) => {
  if (!u.deletionWarningAt) return {};
  const days = Math.floor((Date.now() - new Date(u.deletionWarningAt).getTime()) / (1000 * 60 * 60 * 24));
  if (days >= 3) {
    // Красная окантовка — пора удалять
    return {
      boxShadow: 'inset 4px 0 0 0 #d32f2f',
      backgroundColor: 'rgba(211, 47, 47, 0.06)',
    };
  }
  // Жёлтая окантовка — счётчик идёт
  return {
    boxShadow: 'inset 4px 0 0 0 #f59e0b',
    backgroundColor: 'rgba(245, 158, 11, 0.06)',
  };
};
// ==================== Хелперы для тикетов ====================

// Относительное время: «5 мин назад», «вчера», «12 сентября»
const formatRelativeTime = (dateStr: string): string => {
  const date = new Date(dateStr);
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const min = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (min < 1) return 'только что';
  if (min < 60) return `${min} мин назад`;
  if (hours < 24) return `${hours} ч назад`;
  if (days === 1) return 'вчера';
  if (days < 7) return `${days} дн назад`;
  return date.toLocaleDateString('ru-RU', { day: '2-digit', month: 'long' });
};

// Последнее сообщение в тикете (для превью)
const getLastMessage = (t: Ticket): { text: string; from: string; timestamp: string } => {
  if (t.replies && t.replies.length > 0) {
    const last = t.replies[t.replies.length - 1];
    return { text: last.message, from: last.from, timestamp: last.timestamp };
  }
  return { text: t.message, from: 'user', timestamp: t.createdAt };
};

// Группировка дат для чата
const formatDateGroup = (dateStr: string): string => {
  const date = new Date(dateStr);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today.getTime() - 86400000);
  const msgDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  if (msgDate.getTime() === today.getTime()) return 'Сегодня';
  if (msgDate.getTime() === yesterday.getTime()) return 'Вчера';
  return date.toLocaleDateString('ru-RU', { day: '2-digit', month: 'long', year: 'numeric' });
};

// Инициалы для аватарки
const getInitials = (name: string): string => {
  return name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
};

const getRowHighlight = (createdAt?: string) => {
  if (!createdAt) return {};
  const now = new Date();
  const created = new Date(createdAt);
  const diffDays = Math.floor((now.getTime() - created.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return { backgroundColor: 'rgba(46, 204, 113, 0.15)' };
  if (diffDays <= 7) return { backgroundColor: 'rgba(241, 196, 15, 0.15)' };
  return {};
};

  const filteredTickets = useMemo(() => {
    let result = [...tickets];

    // Фильтр по статусу
    if (ticketFilter === 'open') result = result.filter(t => t.status === 'open');
    if (ticketFilter === 'closed') result = result.filter(t => t.status === 'closed');

    // Поиск
    if (ticketSearch.trim()) {
      const q = ticketSearch.toLowerCase();
      result = result.filter(t =>
        t.subject.toLowerCase().includes(q) ||
        t.userName.toLowerCase().includes(q) ||
        t.userEmail.toLowerCase().includes(q) ||
        t.message.toLowerCase().includes(q)
      );
    }

    // Сортировка: свежие сверху (по updatedAt)
    result.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());

    return result;
  }, [tickets, ticketFilter, ticketSearch]);

const filteredUsersForTable = users.filter(u => {
      if (filterOverdue) {
      if (!(u as any).deletionWarningAt) return false;
      const _days = Math.floor((Date.now() - new Date((u as any).deletionWarningAt).getTime()) / (1000 * 60 * 60 * 24));
      if (_days < 3) return false;   
 }
    const matchesSearch = u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (u.companyName && u.companyName.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesPaid =
      filterPaid === 'all' ? true :
      filterPaid === 'paid' ? u.paymentStatus === 'paid' :
      filterPaid === 'pending' ? u.paymentStatus === 'pending' :
      /* unpaid */ (!u.paymentStatus || u.paymentStatus === 'none');
    const matchesPaidCheckbox = !filterPaidCheckbox || u.paid;
    const matchesCompany = filterCompany === 'all' ? true : u.companyId === filterCompany;
    const matchesRole = filterRole === 'all' ? true : u.role === filterRole;
    const matchesBlocked = filterBlocked === 'all' ? true : filterBlocked === 'active' ? !u.blocked : u.blocked;
    return matchesSearch && matchesPaid && matchesPaidCheckbox  && matchesCompany && matchesRole && matchesBlocked;
  }).sort((a, b) => {
    let aVal: any, bVal: any;
    if (sortKey === 'lastActive') {
      aVal = a.lastActive ? new Date(a.lastActive).getTime() : 0;
      bVal = b.lastActive ? new Date(b.lastActive).getTime() : 0;
    } else if (sortKey === 'name') {
      aVal = (a.name || '').toLowerCase();
      bVal = (b.name || '').toLowerCase();
    } else if (sortKey === 'email') {
      aVal = (a.email || '').toLowerCase();
      bVal = (b.email || '').toLowerCase();
    } else if (sortKey === 'storageUsed') {
      aVal = Number(a.storageUsed) || 0;
      bVal = Number(b.storageUsed) || 0;
    } else {
      aVal = new Date(a.createdAt || 0).getTime();
      bVal = new Date(b.createdAt || 0).getTime();
    }
    if (sortDir === 'asc') return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
    return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
  });
  // ==================== Рендер ====================
  if (!currentUser) {
    return <div style={{ padding: 40, textAlign: 'center', fontSize: 16, color: '#64748b' }}>Загрузка панели...</div>;
  }

  return (
    <div style={styles.container}>
      {/* Кнопка возврата на главный дашборд */}
      <button
        onClick={() => navigate('/dashboard')}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          background: 'rgba(255,255,255,0.6)', backdropFilter: 'blur(8px)',
          border: '1px solid rgba(255,255,255,0.4)', borderRadius: 40,
          padding: '8px 20px', fontSize: 14, fontWeight: 500,
          color: '#1a1a2e', cursor: 'pointer', marginBottom: 20,
          transition: 'all 0.2s',
        }}
        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(37,117,252,0.1)'; e.currentTarget.style.borderColor = '#2575fc'; e.currentTarget.style.color = '#2575fc'; }}
        onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.6)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.4)'; e.currentTarget.style.color = '#1a1a2e'; }}
      >
        <ArrowLeft size={18} /> На главный дашборд
      </button>

      {/* Аналитика */}
      <div style={styles.analyticsGrid}>
        <div style={styles.analyticsCard}><div style={styles.analyticsTitle}>Всего пользователей</div><div style={styles.analyticsValue}>{stats.totalUsers}</div></div>
        <div style={styles.analyticsCard}><div style={styles.analyticsTitle}>Активных</div><div style={styles.analyticsValue}>{stats.activeUsers}</div></div>
        <div
          onClick={() => setFilterPaidCheckbox(!filterPaidCheckbox)}
          title="Показать только пользователей с галочкой «Оплачено»"
          style={{
            background: filterPaidCheckbox ? "linear-gradient(135deg, #d4e4ff, #c3d8ff)" : "rgba(255,255,255,0.5)",
            boxShadow: filterPaidCheckbox ? "0 0 20px rgba(37,117,252,0.3)" : "0 8px 16px rgba(0,0,0,0.04)",
            border: filterPaidCheckbox ? "2px solid #2575fc" : "1px solid rgba(255,255,255,0.4)",
            borderRadius: 20, padding: 18, textAlign: "center", cursor: "pointer",
            transition: "all 0.2s",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6, justifyContent: "center" }}>
            <input
              type="checkbox"
              checked={filterPaidCheckbox}
              readOnly
              style={{ width: 18, height: 18, cursor: "pointer", accentColor: "#2575fc" }}
            />
            <span style={{ fontSize: 13, color: "#4a5568", fontWeight: 500 }}>Оплачено</span>
          </div>
          <div style={{ fontSize: 28, fontWeight: 700, color: "#2575fc" }}>{stats.paidUsers}</div>
        </div>
        <div
          style={{ background: "linear-gradient(135deg, #d4edda, #c3e6cb)", boxShadow: "0 0 20px rgba(40,167,69,0.3)", border: "2px solid #28a745", borderRadius: 20, padding: 18, textAlign: "center" }}
          title="Пользователи с платёжным статусом «Оплачен» (paymentStatus = paid)"
        >
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6, justifyContent: "center" }}>
            <span style={{ fontSize: 20 }}>✅</span>
            <span style={{ fontSize: 13, color: "#4a5568", fontWeight: 500 }}>Статус «Оплачен»</span>
          </div>
          <div style={{ fontSize: 28, fontWeight: 700, color: "#28a745" }}>{stats.paidStatusCount}</div>
        </div>
        <div style={styles.analyticsCard}><div style={styles.analyticsTitle}>Активный демо</div><div style={styles.analyticsValue}>{stats.demoActive}</div></div>
        <div style={{ background: "linear-gradient(135deg, #ffd4d4, #ffc3c3)", boxShadow: "0 0 20px rgba(255,77,79,0.3)", border: "2px solid #ff4d4f", borderRadius: 20, padding: 18, textAlign: "center" }}><div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6, justifyContent: "center" }}><span style={{ fontSize: 20 }}>🔴</span><span style={{ fontSize: 13, color: "#4a5568", fontWeight: 500 }}>Открытых тикетов</span></div><div style={{ fontSize: 28, fontWeight: 700, color: "#ff4d4f" }}>{stats.openTickets}</div></div>
        <div style={styles.analyticsCard}><div style={styles.analyticsTitle}>Закрытых тикетов</div><div style={styles.analyticsValue}>{stats.closedTickets}</div></div>
        <div style={{ background: "linear-gradient(135deg, #d4edda, #c3e6cb)", boxShadow: "0 0 20px rgba(40,167,69,0.3)", border: "2px solid #28a745", borderRadius: 20, padding: 18, textAlign: "center" }}><div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6, justifyContent: "center" }}><span style={{ fontSize: 20 }}>🟢</span><span style={{ fontSize: 13, color: "#4a5568", fontWeight: 500 }}>Сейчас онлайн</span></div><div style={{ fontSize: 28, fontWeight: 700, color: "#28a745" }}>{onlineUsers.length}</div></div>
      </div>

      {isMainAdmin && (
// Экспорт
      <div style={styles.exportBar}>
        <div style={styles.dateRange}>
          <label>С: </label><input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={styles.dateInput} />
          <label>По: </label><input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={styles.dateInput} />
          <button onClick={() => exportStatsToExcel()} style={styles.exportButton}>📊 Экспорт в Excel</button>
        </div>
      </div>
      )}

      {/* Табы */}
      <div style={styles.tabs}>
        {checkAccess('users') && (<button style={{ ...styles.tab, ...(activeTab === 'users' && styles.activeTab) }} onClick={() => setActiveTab('users')}>Пользователи</button>)}
        {checkAccess('companies') && (<button style={{ ...styles.tab, ...(activeTab === 'companies' && styles.activeTab) }} onClick={() => setActiveTab('companies')}>Компании</button>)}
        {checkAccess('billing') && (
          <button style={{ ...styles.tab, ...(activeTab === 'billing' && styles.activeTab) }} onClick={() => setActiveTab('billing')}>💳 Биллинг</button>
        )}
        {checkAccess('tickets') && (<button style={{ ...styles.tab, ...(activeTab === 'tickets' && styles.activeTab) }} onClick={() => setActiveTab('tickets')}>
          Тикеты {stats.openTickets > 0 && <span style={styles.badge}>{stats.openTickets}</span>}
        </button>)}
        {isMainAdmin && (
          <button style={{ ...styles.tab, ...(activeTab === 'accessControl' && styles.activeTab) }} onClick={() => setActiveTab('accessControl')}>🔐 Управление доступом</button>
        )}
        {checkAccess('seo') && (<button style={{ ...styles.tab, ...(activeTab === 'seo' && styles.activeTab) }} onClick={() => setActiveTab('seo')}>🔍 SEO Центр</button>)}
{checkAccess('xml') && (<button style={{ ...styles.tab, ...(activeTab === 'xml' && styles.activeTab) }} onClick={() => setActiveTab('xml')}>XML</button>)}
        {checkAccess('inactiveEmails') && (<button style={{ ...styles.tab, ...(activeTab === 'inactiveEmails' && styles.activeTab) }} onClick={() => setActiveTab('inactiveEmails')}>Рассылки</button>)}
        {checkAccess('promo') && (
          <button
            style={{ ...styles.tab, ...(activeTab === 'promo' && styles.activeTab), position: 'relative' }}
            onClick={() => {
              setActiveTab('promo');
              setPromoBadgeCount(0);
              localStorage.setItem('promoBadgeRead', String(promoTotalActivated));
            }}
          >
            🎁 Промо 14 дней
            {promoBadgeCount > 0 && (
              <span style={{
                marginLeft: 6, display: 'inline-block', minWidth: 18, height: 18,
                padding: '0 5px', background: '#ef4444', color: '#fff',
                borderRadius: 100, fontSize: 10, fontWeight: 700,
                lineHeight: '18px', textAlign: 'center', verticalAlign: 'middle',
              }}>
                {promoBadgeCount}
              </span>
            )}
          </button>
        )}
{checkAccess('archive') && (<button style={{ ...styles.tab, ...(activeTab === 'archive' && styles.activeTab) }} onClick={() => setActiveTab('archive')}>📦 Архив</button>)}
        {checkAccess('systemErrors') && (<button
          style={{ ...styles.tab, ...(activeTab === 'systemErrors' && styles.activeTab) }}
          onClick={() => setActiveTab('systemErrors')}
        >
          🚨 Ошибки системы
          {systemErrorsCount > 0 && (
            <span style={{
              marginLeft: 8, background: '#dc2626', color: '#fff',
              borderRadius: 12, padding: '1px 8px', fontSize: 11, fontWeight: 700,
            }}>
              {systemErrorsCount}
            </span>
          )}
        </button>)}
        {checkAccess('monitoring') && (<button
          style={{ ...styles.tab, ...(activeTab === 'monitoring' && styles.activeTab) }}
          onClick={() => setActiveTab('monitoring')}
        >
          📊 Мониторинг
        </button>)}
        {checkAccess('audit') && (<button
          style={{ ...styles.tab, ...(activeTab === 'audit' && styles.activeTab) }}
          onClick={() => setActiveTab('audit')}
        >
          <span>📋 Аудит ПДн</span>
        </button>)}
      </div>

      {/* ========== ПОЛЬЗОВАТЕЛИ ========== */}
      {activeTab !== 'accessControl' && (
      <ProtectedSection key={activeTab} sectionId={activeTab}>
      {activeTab === 'inactiveEmails' && checkAccess('inactiveEmails') && <InactiveUsersEmail />}
      {activeTab === 'promo' && currentUser?.role === 'superadmin' && <PromoAnalyticsPanel />}
{activeTab === 'archive' && checkAccess('archive') && <ArchivePanel />}
      {activeTab === 'systemErrors' && checkAccess('systemErrors') && <SystemErrorsPanel />}
      {activeTab === 'monitoring' && checkAccess('monitoring') && <SystemMonitoring />}
      {activeTab === 'audit' && checkAccess('audit') && <AuditPanel />}
      {activeTab === 'users' && checkAccess('users') && (
        <>
          <div style={styles.header}>
            <h3>Пользователи</h3>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => fetchUsers()} style={styles.refreshButton} disabled={loading}>🔄 Обновить</button>
              <button onClick={() => setShowAddModal(true)} style={styles.addButton}>+ Добавить</button>
            </div>
          </div>

          <div style={styles.filters}>
            <input type="text" placeholder="Поиск..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} style={styles.searchInput} />
            <select value={filterCompany} onChange={e => setFilterCompany(e.target.value)} style={styles.filterSelect}>
              <option value="all">Все компании</option>
              {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <select value={filterPaid} onChange={e => setFilterPaid(e.target.value as any)} style={styles.filterSelect}>
              <option value="all">Оплата: все</option>
              <option value="paid">Оплачен</option>
              <option value="pending">В оплате</option>
              <option value="unpaid">Не оплачен</option>
</select>
            <select value={filterRole} onChange={e => setFilterRole(e.target.value as any)} style={styles.filterSelect}>
              <option value="all">Роль: все</option>
              <option value="superadmin">Супер Админ</option>
              <option value="admin">Администратор</option>
              <option value="hr_manager">HR-менеджер</option>
              <option value="recruiter">Рекрутер</option>
              <option value="employee">Сотрудник</option>
                              <option value="sales_manager">Менеджер по продажам</option>
                              <option value="accountant">Бухгалтер</option>
                              <option value="kdp">КДП</option>
              <option value="user">Пользователь</option>

            </select>
            <select value={filterBlocked} onChange={e => setFilterBlocked(e.target.value as any)} style={styles.filterSelect}>
              <option value="all">Статус: все</option>
              <option value="active">Активен</option>
              <option value="blocked">Заблокирован</option>
            </select>
            <label style={{ fontSize: 13, color: '#666', display: 'flex', alignItems: 'center', gap: 4, marginLeft: 8 }}>
              <input type="checkbox" checked={filterOverdue} onChange={e => setFilterOverdue(e.target.checked)} />
              Просроченные (более 3 дней)
            </label>
          </div>

          {loading ? <p>Загрузка...</p> : (
            <div style={{ overflowX: 'auto', width: '100%', marginTop: 12 }}>
              {selectedUserIds.size > 0 && (
                <div style={{ display: 'flex', gap: 8, marginTop: 12, marginBottom: 8, flexWrap: 'wrap', alignItems: 'center', padding: '10px 14px', background: 'rgba(37,117,252,0.08)', borderRadius: 12 }}>
                  <span style={{ fontSize: 13, color: '#2575fc', fontWeight: 600 }}>
                    Выбрано: {selectedUserIds.size}
                  </span>
                  <button onClick={() => handleBulkBlock(true)} style={{...styles.addButton, backgroundColor:'#ff4d4f'}}>Заблокировать</button>
                  <button onClick={() => handleBulkBlock(false)} style={{...styles.addButton, backgroundColor:'#28a745'}}>Разблокировать</button>
                  <button onClick={handleBulkDelete} style={{...styles.addButton, backgroundColor:'#ff7875'}}>Удалить</button>
                  <select onChange={e => handleBulkAssignCompany(e.target.value)} defaultValue="" style={styles.filterSelect}>
                    <option value="" disabled>Назначить компанию</option>
                    {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              )}
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={{...styles.th, width: 40}}><input type='checkbox' onChange={toggleAllUsers} checked={selectedUserIds.size === filteredUsersForTable.length && filteredUsersForTable.length > 0} /></th>
                    <th style={styles.th}>ID</th>
                    <th style={styles.th}>Имя</th>
                    <th style={styles.th}>Онлайн</th>
                    <th style={styles.th}>Email</th>
                    <th style={styles.th}>Компания</th>
                    <th style={styles.th}>Роль</th>
                    <th style={styles.th}>Статус</th>
                    <th style={styles.th}>Оплата</th>
                    <th style={styles.th}>Тариф</th>
                    <th style={styles.th}>Окончание</th>
                    <th style={styles.th}>Демо до</th>
                    <th
                      style={{ ...styles.th, cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('lastActive')}
                      title="Сортировать по последней активности"
                    >
                      Последняя активность {sortKey === 'lastActive' ? (sortDir === 'desc' ? '▼' : '▲') : ''}
                    </th>
                    {/* НОВЫЙ СТОЛБЕЦ */}
                    <th style={styles.th}>Смена пароля</th>
                    <th style={styles.th}>Удаление</th>
                    <th
                    style={{ ...styles.th, cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort('storageUsed')}
                    title="Сортировать по занятому месту"
                  >
                    Хранилище {sortKey === 'storageUsed' ? (sortDir === 'desc' ? '▼' : '▲') : ''}
                  </th>
                  <th style={{ ...styles.th, width: 140 }}>Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsersForTable.map(u => {
                    const planLabel = u.tariffPlan === 'month' ? 'Мес' : u.tariffPlan === 'quarter' ? 'Кв' : u.tariffPlan === 'year' ? 'Год' : '—';
                    const paidUntilDate = u.paidUntil ? new Date(u.paidUntil) : null;
                    const isExpired = paidUntilDate && paidUntilDate < new Date();
                    return (
                      <tr key={u.id} style={{ ...styles.tr, ...getRowHighlight(u.createdAt), ...getDeletionStyle(u) }}>
                        <td style={{...styles.td, textAlign: 'center'}}><input type='checkbox' checked={selectedUserIds.has(u.id)} onChange={() => toggleUserSelection(u.id)} /></td>
                        <td style={styles.td}>{u.id.slice(0, 6)}…</td>
                        <td style={styles.td}>{u.name}</td>
                        <td style={{ ...styles.td, textAlign: "center" }}>{onlineUsers.includes(u.id) ? <span style={{ color: "#28a745", fontSize: 18 }}>●</span> : <span style={{ color: "#ccc", fontSize: 18 }}>●</span>}</td>
                        <td style={styles.td}>{u.email}</td>
                        <td style={styles.td}>{u.companyName || u.company}</td>
                        <td style={styles.td}>
                          <select value={u.role} onChange={e => handleRoleChange(u.id, e.target.value)} disabled={u.id === currentUser?.id} style={styles.selectSmall}>
                          {currentUser?.role === 'superadmin' ? (
                            <>
                              <option value="superadmin">Супер Админ</option>
                              <option value="admin">Администратор</option>
                              <option value="hr_manager">HR-менеджер</option>
                              <option value="recruiter">Рекрутер</option>
                              <option value="employee">Сотрудник</option>
                              <option value="sales_manager">Менеджер по продажам</option>
                              <option value="accountant">Бухгалтер</option>
                              <option value="kdp">КДП</option>
                            </>
                          ) : (
                            <>
                              <option value="hr_manager">HR-менеджер</option>
                              <option value="recruiter">Рекрутер</option>
                              <option value="employee">Сотрудник</option>
                              <option value="sales_manager">Менеджер по продажам</option>
                              <option value="accountant">Бухгалтер</option>
                              <option value="kdp">КДП</option>
                            </>
                          )}
                        </select>
                        </td>
                        <td style={styles.td}><span style={{ color: u.blocked ? '#ff4d4f' : '#52c41a', fontWeight: 600 }}>{u.blocked ? 'Заблок.' : 'Активен'}</span></td>
                        <td style={styles.td}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <input type="checkbox" checked={u.paid} onChange={() => handlePaidToggle(u.id)} />
                            <select value={u.paymentStatus || 'none'} onChange={e => handlePaymentStatusChange(u.id, e.target.value)} style={{ ...styles.selectSmall, width: 80 }}>
                              <option value="none">Нет</option>
                              <option value="pending">В оплате</option>
                              <option value="paid">Оплачен</option>
                            </select>
                          </div>
                        </td>
                        <td style={styles.td}>{planLabel}</td>
                        <td style={{ ...styles.td, color: isExpired ? '#ff4d4f' : '#1a1a2e' }}>
                          {paidUntilDate ? paidUntilDate.toLocaleDateString() : '—'}
                        </td>
                        <td style={styles.td}>{u.demoExpiresAt ? new Date(u.demoExpiresAt).toLocaleDateString() : '—'}</td>
                        <td style={styles.td}>{u.lastActive ? new Date(u.lastActive).toLocaleString() : '—'}</td>
                        {/* ИНДИКАТОР СМЕНЫ ПАРОЛЯ */}
                        <td style={{ ...styles.td, textAlign: 'center' }}>

                              {u.passwordChangedAt && u.createdAt && (new Date(u.passwordChangedAt).getTime() - new Date(u.createdAt).getTime()) > 60000 ? (
                              <span
                              style={{ color: '#28a745', fontWeight: 500, cursor: 'help' }}
                              title={`Пароль изменён: ${new Date(u.passwordChangedAt).toLocaleString()}`}
                            >
                              ✅ {new Date(u.passwordChangedAt).toLocaleDateString()}
                            </span>
                          ) : (
                            <span
                              style={{ color: '#ccc', cursor: 'help' }}
                              title="Пароль не менялся"
                            >
                              ⚪
                            </span>
                          )}
                        </td>

                          <td style={{ ...styles.td, textAlign: 'center' }}>
                          {u.deletionWarningAt ? (() => {
                            const days = Math.floor((Date.now() - new Date(u.deletionWarningAt).getTime()) / (1000 * 60 * 60 * 24));
                            const daysLeft = Math.max(0, 3 - days);
                            if (daysLeft === 0) {
                              return (
                                <span
                                  style={{ color: '#d32f2f', fontWeight: 700, fontSize: 12 }}
                                  title={`Пора удалять! Отправлено: ${new Date(u.deletionWarningAt).toLocaleString()}`}
                                >
                                  🔴 Удалить
                                </span>
                              );
                            }
                            return (
                              <span
                                style={{ color: '#f59e0b', fontWeight: 600, fontSize: 12 }}
                                title={`Осталось ${daysLeft} дн. Отправлено: ${new Date(u.deletionWarningAt).toLocaleString()}`}
                              >
                                ⏱ {daysLeft} дн.
                              </span>
                            );
                          })() : (
                            <span style={{ color: '#ccc' }}>—</span>
                          )}
                        </td>

                          <td style={styles.td}>
                          <StorageCell entity={u} type="user" onUpdate={() => fetchUsers(true)} />
                        </td>
                        <td style={styles.td}>
                          <div style={styles.actionIcons}>
                            <button onClick={() => handleBlockToggle(u.id)} style={styles.iconButton} title={u.blocked ? 'Разблокировать' : 'Заблокировать'}>
                              {u.blocked ? <Unlock size={14} /> : <Lock size={14} />}
                            </button>
                            <button onClick={() => handleDelete(u.id)} style={{ ...styles.iconButton, color: '#ff4d4f' }} title="Удалить"><Trash2 size={14} /></button>
                            <button onClick={() => handleResetPassword(u.id)} style={styles.iconButton} title="Сбросить пароль"><KeyRound size={14} /></button>
                            <button onClick={() => openEditModal(u)} style={styles.iconButton} title="Редактировать"><Pencil size={14} /></button>
                            <button onClick={() => extendDemo(u.id)} style={styles.iconButton} title="Продлить демо"><Gift size={14} /></button>
                            {u.paid && (
                              <button onClick={() => extendPaidPeriod(u.id)} style={styles.iconButton} title="Продлить тариф"><Clock size={14} /></button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* ========== КОМПАНИИ ========== */}
      {activeTab === 'companies' && checkAccess('companies') && (
        <>
          <div style={styles.header}>
            <h3>Компании</h3>
            <button onClick={() => setShowCompanyModal(true)} style={styles.addButton}>+ Создать</button>
          </div>
          <div style={{ marginBottom: 16 }}>
            <input
              type="text"
              placeholder="Поиск по компаниям..."
              value={companySearch}
              onChange={(e) => setCompanySearch(e.target.value)}
              style={{
                padding: '10px 16px',
                borderRadius: 20,
                border: '1px solid rgba(0,0,0,0.1)',
                width: '100%',
                maxWidth: 400,
                fontSize: 14,
                background: 'rgba(255,255,255,0.7)',
                outline: 'none',
                transition: 'all 0.2s',
              }}
            />
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={styles.table}>
              <thead><tr><th style={styles.th}>ID</th><th style={styles.th}>Название</th><th style={styles.th}>Создана</th><th style={{ ...styles.th, cursor: 'pointer', userSelect: 'none' }} onClick={() => setCompanySortDir(prev => prev === 'desc' ? 'asc' : prev === 'asc' ? null : 'desc')} title="Сортировать по количеству пользователей">Пользователи {companySortDir === 'desc' ? '▼' : companySortDir === 'asc' ? '▲' : ''}</th><th
                      style={{ ...styles.th, cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => {
                        if (companySortKey !== 'storageUsed') { setCompanySortKey('storageUsed'); setCompanySortDir('desc'); }
                        else setCompanySortDir(prev => prev === 'desc' ? 'asc' : prev === 'asc' ? null : 'desc');
                      }}
                      title="Сортировать по занятому месту"
                    >
                      Хранилище {companySortKey === 'storageUsed' && companySortDir === 'desc' ? '▼' : companySortKey === 'storageUsed' && companySortDir === 'asc' ? '▲' : ''}
                    </th>
                    <th style={{ ...styles.th, width: 100 }}>Действия</th></tr></thead>
              <tbody>
                {companies
                  .filter(c => c.name.toLowerCase().includes(companySearch.toLowerCase()))
                  .map(c => ({ ...c, userCount: users.filter(u => u.companyId === c.id).length }))
                  .sort((a, b) => {
                    if (!companySortDir) return 0;
                    return companySortDir === 'desc' ? b.userCount - a.userCount : a.userCount - b.userCount;
                  })
                  .map(c => (
                  <tr key={c.id} style={styles.tr}>
                    <td style={styles.td}>{c.id}</td>
                    <td style={styles.td}>{c.name}</td>
                    <td style={styles.td}>{new Date(c.createdAt).toLocaleDateString()}</td>
                    <td style={{ ...styles.td, textAlign: 'center' }}>
                      <span
                        onClick={() => setExpandedCompanyId(expandedCompanyId === c.id ? null : c.id)}
                        style={{ cursor: 'pointer', color: (c as any).userCount > 0 ? '#2575fc' : '#ccc', fontWeight: 600 }}
                      >
                        {(c as any).userCount > 0 ? `👥 ${(c as any).userCount}` : '—'}
                      </span>
                      {expandedCompanyId === c.id && (c as any).userCount > 0 && (
                        <div style={{ marginTop: 8, textAlign: 'left', fontSize: 12, maxHeight: 200, overflowY: 'auto', background: 'rgba(37,117,252,0.05)', borderRadius: 8, padding: 8 }}>
                          {users.filter(u => u.companyId === c.id).map(u => (
                            <div key={u.id} style={{ marginBottom: 4 }}>
                              <span style={{ fontWeight: 600 }}>{u.name}</span> — <span style={{ color: '#666' }}>{u.email}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </td>
                    <td style={styles.td}>
                      <StorageCell entity={c} type="company" onUpdate={() => fetchCompanies()} />
                    </td>
                    <td style={styles.td}>
                      <div style={styles.actionIcons}>
                        <button onClick={() => {
  setEditingCompany(c);
  setCompanyForm({ name: c.name });
  setShowCompanyModal(true);
  if (currentUser?.role === 'superadmin') {
    setLoadingCompanyModules(true);
    api.get(`/users/companies/${c.id}/modules`)
      .then(r => setCompanyModules({
        canViewSalesCRM: !!r.data.canViewSalesCRM,
        canViewHRM: !!r.data.canViewHRM,
        canViewAccountingCRM: !!r.data.canViewAccountingCRM,
        canViewHRDocuments: !!r.data.canViewHRDocuments,
        canViewDataDriven: !!r.data.canViewDataDriven,
      }))
      .catch(err => console.warn('Не удалось загрузить модули:', err))
      .finally(() => setLoadingCompanyModules(false));
  }
}} style={styles.iconButton} title="Редактировать"><Pencil size={14} /></button>
                        <button onClick={() => handleDeleteCompany(c.id)} style={{ ...styles.iconButton, color: '#ff4d4f' }} title="Удалить"><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ========== БИЛЛИНГ ========== */}
      {activeTab === 'billing' && checkAccess('billing') && (
        <BillingPanel users={users} />
      )}

      {/* ========== ТИКЕТЫ ========== */}
      {activeTab === 'tickets' && checkAccess('tickets') && (
        <div>
          <h3>Обращения пользователей</h3>
          {!selectedTicket ? (
            <div>
              {/* Фильтры и поиск */}
              <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
                <div style={{ display: 'flex', gap: 4, background: 'rgba(255,255,255,0.6)', padding: 4, borderRadius: 12 }}>
                  {[
                    { key: 'all', label: `Все (${tickets.length})` },
                    { key: 'open', label: `Открытые (${tickets.filter(t => t.status === 'open').length})` },
                    { key: 'closed', label: `Закрытые (${tickets.filter(t => t.status === 'closed').length})` },
                  ].map(f => (
                    <button
                      key={f.key}
                      onClick={() => setTicketFilter(f.key as any)}
                      style={{
                        padding: '8px 16px',
                        borderRadius: 8,
                        border: 'none',
                        background: ticketFilter === f.key ? 'linear-gradient(135deg, #6a11cb, #2575fc)' : 'transparent',
                        color: ticketFilter === f.key ? '#fff' : '#4a4a68',
                        fontWeight: 600,
                        fontSize: 13,
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                      }}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>

                <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
                  <Search size={16} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#999' }} />
                  <input
                    type="text"
                    placeholder="Поиск по теме, имени, email..."
                    value={ticketSearch}
                    onChange={e => setTicketSearch(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px 14px 10px 38px',
                      borderRadius: 12,
                      border: '1px solid rgba(0,0,0,0.1)',
                      background: 'rgba(255,255,255,0.7)',
                      fontSize: 13,
                      outline: 'none',
                    }}
                  />
                </div>
              </div>

              {filteredTickets.length === 0 ? (
                <div style={{ padding: 40, textAlign: 'center', color: '#999', fontSize: 14 }}>
                  {ticketSearch ? 'Ничего не найдено' : 'Нет обращений'}
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {filteredTickets.map(t => {
                    const last = getLastMessage(t);
                    return (
                      <div
                        key={t.id}
                        onClick={() => handleTicketSelect(t)}
                        style={{
                          display: 'flex',
                          gap: 12,
                          padding: '14px 16px',
                          background: 'rgba(255,255,255,0.7)',
                          borderRadius: 16,
                          cursor: 'pointer',
                          borderLeft: t.status === 'open' ? '4px solid #ff4d4f' : '4px solid transparent',
                          border: '1px solid rgba(0,0,0,0.05)',
                          transition: 'all 0.2s',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                        }}
                        onMouseEnter={e => {
                          e.currentTarget.style.transform = 'translateY(-1px)';
                          e.currentTarget.style.boxShadow = '0 6px 16px rgba(37,117,252,0.15)';
                        }}
                        onMouseLeave={e => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.04)';
                        }}
                      >
                        <div style={{
                          width: 44, height: 44, borderRadius: '50%',
                          background: t.status === 'open' ? 'linear-gradient(135deg, #ff4d4f, #ff7875)' : 'linear-gradient(135deg, #6a11cb, #2575fc)',
                          color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontWeight: 700, fontSize: 14, flexShrink: 0,
                        }}>
                          {getInitials(t.userName)}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                            <span style={{ fontWeight: 600, fontSize: 14, color: '#1a1a2e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {t.subject || '(без темы)'}
                            </span>
                            <span style={{ fontSize: 11, color: '#999', flexShrink: 0 }}>
                              {formatRelativeTime(t.updatedAt)}
                            </span>
                          </div>
                          <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>
                            {t.userName} <span style={{ color: '#aaa' }}>· {t.userEmail}</span>
                          </div>
                          <div style={{ fontSize: 13, color: '#4a4a68', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {last.from === 'admin' && <span style={{ color: '#2575fc', fontWeight: 600 }}>Вы: </span>}
                            {last.text}
                          </div>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'space-between', gap: 6 }}>
                          <span style={{
                            padding: '2px 8px', borderRadius: 8, fontSize: 11, fontWeight: 600,
                            background: t.status === 'open' ? 'rgba(255,77,79,0.1)' : 'rgba(82,196,26,0.1)',
                            color: t.status === 'open' ? '#ff4d4f' : '#52c41a',
                          }}>
                            {t.status === 'open' ? 'Открыт' : 'Закрыт'}
                          </span>
                          {t.replies && t.replies.length > 0 && (
                            <span style={{ fontSize: 11, color: '#999', display: 'flex', alignItems: 'center', gap: 3 }}>
                              <MessageCircle size={12} /> {t.replies.length}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (

            <div>
            
              {/* Шапка чата */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
                <button
                  onClick={() => setSelectedTicket(null)}
                  style={{ background: 'rgba(255,255,255,0.6)', border: '1px solid rgba(0,0,0,0.1)', borderRadius: 10, padding: '6px 10px', cursor: 'pointer', color: '#2575fc', display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, fontWeight: 600 }}
                >
                  <ArrowLeft size={14} /> Назад
                </button>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <h4 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#1a1a2e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {selectedTicket.subject || '(без темы)'}
                  </h4>
                  <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>
                    {selectedTicket.userName} · {selectedTicket.userEmail}
                  </div>
                </div>
                <span style={{
                  padding: '4px 12px', borderRadius: 10, fontSize: 12, fontWeight: 600,
                  background: selectedTicket.status === 'open' ? 'rgba(255,77,79,0.1)' : 'rgba(82,196,26,0.1)',
                  color: selectedTicket.status === 'open' ? '#ff4d4f' : '#52c41a',
                }}>
                  {selectedTicket.status === 'open' ? 'Открыт' : 'Закрыт'}
                </span>
              </div>

              {/* Сообщения (чат) */}
              <div style={{
                background: 'rgba(255,255,255,0.5)',
                borderRadius: 16,
                padding: 16,
                minHeight: 300,
                maxHeight: 500,
                overflowY: 'auto',
                marginBottom: 16,
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
              }}>
                {/* Первое сообщение пользователя */}
                {selectedTicket.message && (
                  <div style={{ display: 'flex', gap: 8 }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                      background: 'linear-gradient(135deg, #6a11cb, #2575fc)',
                      color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontWeight: 700, fontSize: 12,
                    }}>
                      {getInitials(selectedTicket.userName)}
                    </div>
                    <div style={{ maxWidth: '75%' }}>
                      <div style={{
                        padding: '10px 14px',
                        background: '#fff',
                        borderRadius: '4px 16px 16px 16px',
                        border: '1px solid rgba(0,0,0,0.06)',
                        fontSize: 14,
                        color: '#1a1a2e',
                        lineHeight: 1.5,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}>
                        {selectedTicket.message}
                      </div>
                      <div style={{ fontSize: 10, color: '#999', marginTop: 4 }}>
                        {new Date(selectedTicket.createdAt).toLocaleString('ru-RU')}
                      </div>
                    </div>
                  </div>
                )}

                {/* Ответы */}
                {selectedTicket.replies?.map((r, idx) => {
                  const isAdmin = r.from === 'admin';
                  return (
                    <div key={idx} style={{ display: 'flex', gap: 8, flexDirection: isAdmin ? 'row-reverse' : 'row' }}>
                      <div style={{
                        width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                        background: isAdmin ? 'linear-gradient(135deg, #22c55e, #16a34a)' : 'linear-gradient(135deg, #6a11cb, #2575fc)',
                        color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontWeight: 700, fontSize: 12,
                      }}>
                        {isAdmin ? 'Адм' : getInitials(selectedTicket.userName)}
                      </div>
                      <div style={{ maxWidth: '75%' }}>
                        <div style={{
                          padding: '10px 14px',
                          background: isAdmin ? 'linear-gradient(135deg, #6a11cb, #2575fc)' : '#fff',
                          color: isAdmin ? '#fff' : '#1a1a2e',
                          borderRadius: isAdmin ? '16px 4px 16px 16px' : '4px 16px 16px 16px',
                          border: isAdmin ? 'none' : '1px solid rgba(0,0,0,0.06)',
                          fontSize: 14,
                          lineHeight: 1.5,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                        }}>
                          {r.message}
                        </div>
                        <div style={{ fontSize: 10, color: '#999', marginTop: 4, textAlign: isAdmin ? 'right' : 'left' }}>
                          {isAdmin ? 'Администратор' : selectedTicket.userName} · {new Date(r.timestamp).toLocaleString('ru-RU')}
                        </div>
                      </div>
                    </div>
                  );
                })}

                <div ref={messagesEndRef} />
              </div>

              {/* Поле ответа */}
              {selectedTicket.status === 'open' && (
                <div style={{ marginBottom: 12 }}>
                  <textarea
                    placeholder="Напишите ответ... (Enter — отправить, Shift+Enter — новая строка)"
                    rows={3}
                    value={adminReply}
                    onChange={e => setAdminReply(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendAdminReply();
                      }
                    }}
                    style={{
                      width: '100%',
                      padding: 12,
                      borderRadius: 12,
                      border: '1px solid rgba(0,0,0,0.1)',
                      background: 'rgba(255,255,255,0.9)',
                      fontSize: 14,
                      outline: 'none',
                      resize: 'vertical',
                      fontFamily: 'inherit',
                      boxSizing: 'border-box',
                    }}
                  />
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                    <button
                      onClick={handleSendAdminReply}
                      disabled={!adminReply.trim()}
                      style={{
                        ...styles.addButton,
                        background: adminReply.trim() ? 'linear-gradient(135deg, #6a11cb, #2575fc)' : '#ccc',
                        display: 'flex', alignItems: 'center', gap: 6,
                        opacity: adminReply.trim() ? 1 : 0.6,
                        cursor: adminReply.trim() ? 'pointer' : 'not-allowed',
                      }}
                    >
                      <Send size={14} /> Отправить
                    </button>
                  </div>
                </div>
              )}

              {/* Действия */}
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                {selectedTicket.status === 'open' ? (
                  <button onClick={handleCloseTicket} style={{ ...styles.addButton, background: '#ff4d4f' }}>
                    Закрыть тикет
                  </button>
                ) : (
                  <button onClick={handleReopenTicket} style={{ ...styles.addButton, background: '#52c41a' }}>
                    Открыть заново
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ========== XML ========== */}
      {activeTab === 'seo' && checkAccess('seo') && (
        <SEOManager />
      )}

      {activeTab === 'xml' && checkAccess('xml') && (
        <TransferTab companies={companies} showAlert={showAlert} />
      )}

      </ProtectedSection>
      )}

            {activeTab === 'accessControl' && (
        <AccessControl />
      )}

      {/* ========== МОДАЛЬНЫЕ ОКНА ========== */}
      {showAddModal && (
        <div style={modalStyles.overlay} onClick={() => setShowAddModal(false)}>
          <div style={modalStyles.modal} onClick={e => e.stopPropagation()}>
            <h3 style={modalStyles.title}>Добавление пользователя</h3>
            <input type="text" placeholder="Имя" value={newUser.name} onChange={e => setNewUser({ ...newUser, name: e.target.value })} style={modalStyles.input} />
            <input type="email" placeholder="Email" value={newUser.email} onChange={e => setNewUser({ ...newUser, email: e.target.value })} style={modalStyles.input} />
            <input type="password" placeholder="Пароль" value={newUser.password} onChange={e => setNewUser({ ...newUser, password: e.target.value })} style={modalStyles.input} />
            <select value={newUser.companyId} onChange={e => setNewUser({ ...newUser, companyId: e.target.value })} style={modalStyles.select}>
              <option value="">Выберите компанию</option>
              {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <select value={newUser.role} onChange={e => setNewUser({ ...newUser, role: e.target.value as any })} style={modalStyles.select}>
                      {currentUser?.role === 'superadmin' ? (
                        <>
                          <option value="admin">Администратор</option>
                          <option value="hr_manager">HR-менеджер</option>
                          <option value="recruiter">Рекрутер</option>
                          <option value="employee">Сотрудник</option>
                              <option value="sales_manager">Менеджер по продажам</option>
                              <option value="accountant">Бухгалтер</option>
                              <option value="kdp">КДП</option>
                          <option value="user">Пользователь</option>
                          <option value="superadmin">Супер Админ</option>
                        </>
                      ) : (
                        <>
                          <option value="hr_manager">HR-менеджер</option>
                          <option value="recruiter">Рекрутер</option>
                          <option value="employee">Сотрудник</option>
                              <option value="sales_manager">Менеджер по продажам</option>
                              <option value="accountant">Бухгалтер</option>
                              <option value="kdp">КДП</option>
                          <option value="user">Пользователь</option>
                        </>
                      )}
                    </select>
            <select value={newUser.tariffPlan} onChange={e => setNewUser({ ...newUser, tariffPlan: e.target.value as any })} style={modalStyles.select}>
              <option value="month">Месяц</option>
              <option value="quarter">Квартал</option>
              <option value="year">Год</option>
            </select>
            <input type="date" placeholder="Дата окончания тарифа" value={newUser.paidUntil} onChange={e => setNewUser({ ...newUser, paidUntil: e.target.value })} style={modalStyles.input} />
            <input type="date" placeholder="Демо до" value={newUser.demoExpiresAt} onChange={e => setNewUser({ ...newUser, demoExpiresAt: e.target.value })} style={modalStyles.input} />
            <select value={newUser.paymentStatus} onChange={e => setNewUser({ ...newUser, paymentStatus: e.target.value as any })} style={modalStyles.select}>
              <option value="none">Не оплачен</option>
              <option value="pending">В оплате</option>
              <option value="paid">Оплачен</option>
            </select>
            <div style={modalStyles.buttons}>
              <button onClick={handleAddUser} style={modalStyles.submitButton}>Добавить</button>
              <button onClick={() => setShowAddModal(false)} style={modalStyles.cancelButton}>Отмена</button>
            </div>
          </div>
        </div>
      )}

      {showEditModal && editingUser && (
        <div style={modalStyles.overlay} onClick={() => setShowEditModal(false)}>
          <div style={modalStyles.modal} onClick={e => e.stopPropagation()}>
            <h3 style={modalStyles.title}>Редактирование пользователя</h3>
            <input type="text" placeholder="Имя" value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} style={modalStyles.input} />
            <input type="email" placeholder="Email" value={editForm.email} onChange={e => setEditForm({ ...editForm, email: e.target.value })} style={modalStyles.input} />
            <div style={{ position: 'relative', marginBottom: 12 }}>
              <input
                type={showEditPassword ? 'text' : 'password'}
                placeholder="Новый пароль (оставьте пустым)"
                value={editForm.password}
                onChange={e => setEditForm({ ...editForm, password: e.target.value })}
                style={{ ...modalStyles.input, marginBottom: 0, paddingRight: 40 }}
              />
              <button type="button" onClick={() => setShowEditPassword(!showEditPassword)}
                style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'transparent', border: 'none', cursor: 'pointer', color: '#666' }}>
                {showEditPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            <select value={editForm.companyId} onChange={e => { const comp = companies.find(c => c.id === e.target.value); setEditForm({ ...editForm, companyId: e.target.value, company: comp ? comp.name : "" }); }} style={modalStyles.select}>
              <option value="">Выберите компанию</option>
              {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <select value={editForm.role} onChange={e => setEditForm({ ...editForm, role: e.target.value as any })} style={modalStyles.select}>
                      {currentUser?.role === 'superadmin' ? (
                        <>
                          <option value="superadmin">Супер Админ</option>
                          <option value="admin">Администратор</option>
                          <option value="hr_manager">HR-менеджер</option>
                          <option value="recruiter">Рекрутер</option>
                          <option value="employee">Сотрудник</option>
                              <option value="sales_manager">Менеджер по продажам</option>
                              <option value="accountant">Бухгалтер</option>
                              <option value="kdp">КДП</option>
                        </>
                      ) : (
                        <>
                          <option value="hr_manager">HR-менеджер</option>
                          <option value="recruiter">Рекрутер</option>
                          <option value="employee">Сотрудник</option>
                              <option value="sales_manager">Менеджер по продажам</option>
                              <option value="accountant">Бухгалтер</option>
                              <option value="kdp">КДП</option>
                        </>
                      )}
                    </select>
            <select value={editTariffPlan} onChange={e => { setEditTariffPlan(e.target.value as any); setEditPaidUntil(recalcPaidUntil(e.target.value as any)); }} style={modalStyles.select}>
              <option value="month">Месяц</option>
              <option value="quarter">Квартал</option>
              <option value="year">Год</option>
            </select>
            <input type="date" placeholder="Дата окончания тарифа" value={editPaidUntil} onChange={e => setEditPaidUntil(e.target.value)} style={modalStyles.input} />
            <input type="date" placeholder="Демо до" value={editDemoExpiresAt} onChange={e => setEditDemoExpiresAt(e.target.value)} style={modalStyles.input} />
            <div style={modalStyles.buttons}>
              <button onClick={handleEditUser} style={modalStyles.submitButton}>Сохранить</button>
              <button onClick={() => setShowEditModal(false)} style={modalStyles.cancelButton}>Отмена</button>
            </div>
          </div>
        </div>
      )}

      {showResetModal && (
        <div style={modalStyles.overlay} onClick={() => setShowResetModal(false)}>
          <div style={modalStyles.modal} onClick={e => e.stopPropagation()}>
            <h3 style={modalStyles.title}>Сброс пароля</h3>
            <p style={{ marginBottom: 12, color: '#666' }}>Введите новый пароль.</p>
            <input type="password" placeholder="Новый пароль" value={newPassword} onChange={e => setNewPassword(e.target.value)} style={modalStyles.input} autoFocus />
            <div style={modalStyles.buttons}>
              <button onClick={confirmResetPassword} style={modalStyles.submitButton}>Сохранить</button>
              <button onClick={() => setShowResetModal(false)} style={modalStyles.cancelButton}>Отмена</button>
            </div>
          </div>
        </div>
      )}

      {showCompanyModal && (
        <div style={modalStyles.overlay} onClick={() => setShowCompanyModal(false)}>
          <div style={modalStyles.modal} onClick={e => e.stopPropagation()}>
            <h3 style={modalStyles.title}>{editingCompany ? 'Редактировать компанию' : 'Новая компания'}</h3>
            <input type="text" placeholder="Название" value={companyForm.name} onChange={e => setCompanyForm({ name: e.target.value })} style={modalStyles.input} />
            {currentUser?.role === 'superadmin' && (
              <div style={{ marginTop: 16, padding: 16, background: '#f8fafc', borderRadius: 12, border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#1a1a2e', marginBottom: 12 }}>🔐 Доступ к модулям</div>
                {loadingCompanyModules ? (
                  <div style={{ fontSize: 12, color: '#94a3b8' }}>Загрузка...</div>
                ) : (
                  <ModuleAccessPicker
                    value={companyModules}
                    onChange={(next) => setCompanyModules({ ...companyModules, ...next })}
                  />
                )}
              </div>
            )}
            <div style={modalStyles.buttons}>
              <button onClick={editingCompany ? handleUpdateCompany : handleAddCompany} style={modalStyles.submitButton}>Сохранить</button>
              <button onClick={() => { setShowCompanyModal(false); setEditingCompany(null); setCompanyForm({ name: '' }); }} style={modalStyles.cancelButton}>Отмена</button>
            </div>
          </div>
        </div>
      )}
      {showCompanyDeleteModal && deleteCompanyData && (
        <div style={modalStyles.overlay} onClick={() => !deleteCompanyLoading && setShowCompanyDeleteModal(false)}>
          <div style={modalStyles.modal} onClick={e => e.stopPropagation()}>
            <h3 style={{ ...modalStyles.title, color: '#d32f2f' }}>
              ⚠️ Удаление компании «{deleteCompanyData.name}»
            </h3>

            <p style={{ fontSize: 14, color: '#4a4a68', margin: '12px 0 16px', lineHeight: 1.5 }}>
              К компании привязаны следующие сущности. При удалении со всеми данными они будут <b>безвозвратно удалены</b>:
            </p>

            <div style={{
              background: 'rgba(255, 77, 79, 0.08)',
              border: '1px solid rgba(255, 77, 79, 0.3)',
              borderRadius: 12,
              padding: '10px 14px',
              marginBottom: 16,
              maxHeight: 250,
              overflowY: 'auto',
              fontSize: 13,
              textAlign: 'left',
            }}>
              {deleteCompanyData.related.employees?.count > 0 && (
                <div style={{ marginBottom: 8 }}>
                  <b>💼 Сотрудники ({deleteCompanyData.related.employees.count}):</b>
                  <ul style={{ margin: '4px 0 0 0', paddingLeft: 20 }}>
                    {deleteCompanyData.related.employees.items.map((e: any, i: number) => (
                      <li key={i}>{e.email || e.name || '—'}</li>
                    ))}
                  </ul>
                </div>
              )}
              {deleteCompanyData.related.candidates?.count > 0 && (
                <div style={{ marginBottom: 8 }}>
                  <b>🎯 Кандидаты ({deleteCompanyData.related.candidates.count}):</b>
                  <ul style={{ margin: '4px 0 0 0', paddingLeft: 20 }}>
                    {deleteCompanyData.related.candidates.items.map((c: any, i: number) => (
                      <li key={i}>{c.name && c.name !== '—' ? c.name : ''}{c.email ? ` — ${c.email}` : ''}</li>
                    ))}
                    {deleteCompanyData.related.candidates.count > 20 && (
                      <li>…и ещё {deleteCompanyData.related.candidates.count - 20}</li>
                    )}
                  </ul>
                </div>
              )}
              {deleteCompanyData.related.vacancies?.count > 0 && (
                <div style={{ marginBottom: 8 }}>
                  <b>📊 Вакансии ({deleteCompanyData.related.vacancies.count}):</b>
                  <ul style={{ margin: '4px 0 0 0', paddingLeft: 20 }}>
                    {deleteCompanyData.related.vacancies.items.map((v: any, i: number) => (
                      <li key={i}>{v.name}</li>
                    ))}
                  </ul>
                </div>
              )}
              {deleteCompanyData.related.requisitions?.count > 0 && (
                <div style={{ marginBottom: 8 }}>
                  <b>📋 Заявки ({deleteCompanyData.related.requisitions.count}):</b>
                  <ul style={{ margin: '4px 0 0 0', paddingLeft: 20 }}>
                    {deleteCompanyData.related.requisitions.items.map((r: any, i: number) => (
                      <li key={i}>{r.name}</li>
                    ))}
                  </ul>
                </div>
              )}
              {deleteCompanyData.related.users?.count > 0 && (
                <div style={{ marginBottom: 8 }}>
                  <b>👤 Пользователи ({deleteCompanyData.related.users.count}):</b>{' '}
                  <span style={{ color: '#666' }}>будут отвязаны (не удалены)</span>
                </div>
              )}
            </div>

            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, fontSize: 13, color: '#d32f2f', fontWeight: 500, cursor: 'pointer', textAlign: 'left' }}>
              <input
                type="checkbox"
                checked={forceDeleteConfirmed}
                onChange={e => setForceDeleteConfirmed(e.target.checked)}
                style={{ width: 18, height: 18, cursor: 'pointer', flexShrink: 0 }}
              />
              Я понимаю, что данные будут удалены безвозвратно
            </label>

            <div style={modalStyles.buttons}>
              <button
                onClick={confirmForceDelete}
                disabled={!forceDeleteConfirmed || deleteCompanyLoading}
                style={{
                  ...modalStyles.submitButton,
                  background: forceDeleteConfirmed ? 'linear-gradient(135deg, #d32f2f, #ff5252)' : '#ccc',
                  opacity: deleteCompanyLoading ? 0.6 : 1,
                  cursor: forceDeleteConfirmed ? 'pointer' : 'not-allowed',
                }}
              >
                {deleteCompanyLoading ? 'Удаление...' : 'Удалить со всеми данными'}
              </button>
              <button
                onClick={() => setShowCompanyDeleteModal(false)}
                disabled={deleteCompanyLoading}
                style={modalStyles.cancelButton}
              >
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}


      {confirmDialog && (
        <div style={modalStyles.overlay} onClick={() => setConfirmDialog(null)}>
          <div style={modalStyles.modal} onClick={e => e.stopPropagation()}>
            <h3 style={modalStyles.title}>{confirmDialog.title}</h3>
            <p style={{ marginBottom: 12, color: '#666' }}>{confirmDialog.message}</p>
            <div style={modalStyles.buttons}>
              <button onClick={() => { confirmDialog.onConfirm(); setConfirmDialog(null); }} style={modalStyles.submitButton}>Да</button>
              <button onClick={() => setConfirmDialog(null)} style={modalStyles.cancelButton}>Отмена</button>
            </div>
          </div>
        </div>
      )}

      {alertDialog && (
        <div style={modalStyles.overlay} onClick={() => setAlertDialog(null)}>
          <div style={modalStyles.modal} onClick={e => e.stopPropagation()}>
            <h3 style={modalStyles.title}>{alertDialog.title}</h3>
            <p style={{ marginBottom: 12, color: '#666' }}>{alertDialog.message}</p>
            <div style={modalStyles.buttons}>
              <button onClick={() => setAlertDialog(null)} style={modalStyles.submitButton}>OK</button>
            </div>
          </div>
        </div>
      )}
      {showBulkDeleteModal && (
        <div style={modalStyles.overlay} onClick={() => !bulkDeleteLoading && setShowBulkDeleteModal(false)}>
          <div style={modalStyles.modal} onClick={e => e.stopPropagation()}>
            <h3 style={{ ...modalStyles.title, color: '#d32f2f' }}>
              ⚠️ Удаление пользователей
            </h3>
            <p style={{ fontSize: 14, color: '#4a4a68', margin: '12px 0 16px', lineHeight: 1.5 }}>
              Вы собираетесь <b>безвозвратно удалить</b> пользователей:
            </p>
            <div style={{
              background: 'rgba(255, 77, 79, 0.08)',
              border: '1px solid rgba(255, 77, 79, 0.3)',
              borderRadius: 12,
              padding: '10px 14px',
              marginBottom: 16,
              maxHeight: 150,
              overflowY: 'auto',
              fontSize: 13,
            }}>
              <b style={{ color: '#d32f2f' }}>Всего: {selectedUserIds.size}</b>
              {selectedUserIds.size <= 10 && (
                <ul style={{ margin: '6px 0 0 0', paddingLeft: 20 }}>
                  {users.filter(u => selectedUserIds.has(u.id)).map(u => (
                    <li key={u.id} style={{ marginBottom: 2 }}>
                      {u.name} — <span style={{ color: '#666' }}>{u.email}</span>
                    </li>
                  ))}
                </ul>
              )}
              {selectedUserIds.size > 10 && (
                <div style={{ marginTop: 6, color: '#666' }}>
                  (список скрыт — слишком много пользователей)
                </div>
              )}
            </div>
            <p style={{ fontSize: 13, color: '#d32f2f', fontWeight: 500, marginBottom: 20 }}>
              Это действие нельзя отменить. Данные будут удалены безвозвратно.
            </p>
            <div style={modalStyles.buttons}>
              <button
                onClick={confirmBulkDelete}
                disabled={bulkDeleteLoading}
                style={{
                  ...modalStyles.submitButton,
                  background: 'linear-gradient(135deg, #d32f2f, #ff5252)',
                  opacity: bulkDeleteLoading ? 0.6 : 1,
                }}
              >
                {bulkDeleteLoading ? 'Удаление...' : `Удалить (${selectedUserIds.size})`}
              </button>
              <button
                onClick={() => setShowBulkDeleteModal(false)}
                disabled={bulkDeleteLoading}
                style={modalStyles.cancelButton}
              >
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== СТИЛИ ====================
const styles: Record<string, React.CSSProperties> = {
  container: {
    background: 'rgba(255,255,255,0.6)',
    borderRadius: 32,
    padding: 28,
    marginBottom: 30,
    boxShadow: '0 20px 40px rgba(0,0,0,0.08)',
    border: '1px solid rgba(255,255,255,0.4)',
    maxWidth: '100%',
    overflowX: 'hidden',
  },
  analyticsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: 16,
    marginBottom: 24,
  },
  analyticsCard: {
    background: 'rgba(255,255,255,0.5)',
    backdropFilter: 'blur(8px)',
    borderRadius: 20,
    padding: 18,
    textAlign: 'center',
    boxShadow: '0 8px 16px rgba(0,0,0,0.04)',
    border: '1px solid rgba(255,255,255,0.4)',
  },
  analyticsTitle: { fontSize: 13, color: '#4a5568', marginBottom: 6, fontWeight: 500 },
  analyticsValue: { fontSize: 28, fontWeight: 700, color: '#1a1a2e' },
  exportBar: { display: 'flex', justifyContent: 'flex-end', marginBottom: 24, alignItems: 'center' },
  dateRange: {
    display: 'flex', gap: 10, alignItems: 'center',
    background: 'rgba(255,255,255,0.4)', padding: '6px 18px', borderRadius: 40,
    border: '1px solid rgba(255,255,255,0.5)',
  },
  dateInput: { padding: '6px 10px', borderRadius: 30, border: '1px solid rgba(0,0,0,0.1)', background: 'rgba(255,255,255,0.7)' },
  exportButton: {
    background: 'linear-gradient(135deg, #27ae60, #20c997)',
    color: 'white', border: 'none', borderRadius: 40, padding: '8px 20px',
    cursor: 'pointer', fontWeight: 600, fontSize: 14,
    boxShadow: '0 8px 16px rgba(40,167,69,0.2)',
  },
  tabs: { display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 20, paddingBottom: 10, borderBottom: '1px solid rgba(0,0,0,0.06)' },
  tab: {
    padding: '8px 18px', background: 'transparent', border: 'none', borderRadius: 40,
    fontSize: 14, fontWeight: 600, cursor: 'pointer', color: '#666',
    transition: 'all 0.2s',
  },
  activeTab: {
    background: 'linear-gradient(135deg, #6a11cb, #2575fc)',
    color: 'white', boxShadow: '0 6px 12px rgba(37,117,252,0.2)',
  },
  badge: {
    display: 'inline-block', marginLeft: 6, backgroundColor: '#ff4d4f',
    color: 'white', borderRadius: 20, padding: '1px 7px', fontSize: 12, lineHeight: 1,
  },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 },
  refreshButton: {
    background: 'transparent', border: '1px solid #2575fc', color: '#2575fc',
    borderRadius: 40, padding: '6px 16px', cursor: 'pointer', fontSize: 13,
    backdropFilter: 'blur(8px)',
  },
  addButton: {
    background: 'linear-gradient(135deg, #6a11cb, #2575fc)',
    color: 'white', border: 'none', borderRadius: 40, padding: '8px 20px',
    fontWeight: 600, cursor: 'pointer', fontSize: 13,
    boxShadow: '0 6px 12px rgba(37,117,252,0.2)',
  },
  filters: { display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' },
  searchInput: {
    flex: '1 1 180px', padding: '10px 18px', borderRadius: 40,
    border: '1px solid rgba(0,0,0,0.08)', background: 'rgba(255,255,255,0.7)',
    fontSize: 13, outline: 'none',
  },
  filterSelect: {
    padding: '10px 14px', borderRadius: 40, border: '1px solid rgba(0,0,0,0.08)',
    background: 'rgba(255,255,255,0.7)', fontSize: 13, minWidth: 130,
  },
  selectSmall: {
    padding: '4px 8px', borderRadius: 20, border: '1px solid rgba(0,0,0,0.1)',
    background: 'rgba(255,255,255,0.9)', fontSize: 12,
  },
  table: { width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: 13 },
  th: {
    padding: '10px 8px', borderBottom: '2px solid rgba(37,117,252,0.15)',
    color: '#2575fc', fontWeight: 600, whiteSpace: 'nowrap',
  },
  td: { padding: '8px', borderBottom: '1px solid rgba(0,0,0,0.05)', verticalAlign: 'middle' },
  tr: { transition: 'background 0.2s' },
  actionIcons: { display: 'flex', gap: 4, flexWrap: 'wrap' },
  iconButton: {
    background: 'rgba(255,255,255,0.6)',
    border: '1px solid rgba(0,0,0,0.1)',
    borderRadius: '50%',
    width: 30,
    height: 30,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer', color: '#1a1a2e',
    transition: 'all 0.2s',
  },
};

const modalStyles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(8px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000,
  },
  modal: {
    background: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(20px)',
    borderRadius: 28, padding: 28, width: 480, maxWidth: '90%',
    maxHeight: '85vh', overflowY: 'auto',
    boxShadow: '0 25px 50px rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.5)',
  },
  title: { fontSize: 20, fontWeight: 700, margin: '0 0 16px 0', color: '#1a1a2e' },
  input: {
    width: '100%', padding: '10px 14px', marginBottom: 12, borderRadius: 16,
    border: '1px solid rgba(0,0,0,0.1)', background: 'rgba(255,255,255,0.8)',
    fontSize: 13, outline: 'none', boxSizing: 'border-box',
  },
  select: {
    width: '100%', padding: '10px 14px', marginBottom: 12, borderRadius: 16,
    border: '1px solid rgba(0,0,0,0.1)', background: 'rgba(255,255,255,0.8)',
    fontSize: 13, appearance: 'none',
  },
  buttons: { display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 },
  submitButton: {
    background: 'linear-gradient(135deg, #6a11cb, #2575fc)',
    color: 'white', border: 'none', borderRadius: 40, padding: '10px 22px',
    cursor: 'pointer', fontWeight: 600, fontSize: 13,
  },
  cancelButton: {
    background: 'transparent', border: '1px solid #2575fc', color: '#2575fc',
    borderRadius: 40, padding: '10px 22px', cursor: 'pointer', fontWeight: 600, fontSize: 13,
  },
};

export default AdminPanel;
